import React, {useCallback, useState} from "react";
import {Button, Card, CardBody, Col, Container, Row} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableExo from "./components/TableExo";
import ModalExo from "./components/ModalExo";
import ModalExoTemplate from "./components/ExoTemplate/ModalExoTemplate";
import ModalExoReplacer from "./components/ExoReplacer/ModalExoReplacer";
import usePermissions from "../../hooks/usePermissions";
import {Navigate} from "react-router-dom";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {ExoService} from "../../services/exo.service";
import NoSearchResult from "../../components/Common/NoSearchResult";
import ServerPagination from "../../components/Common/ServerPagination";

interface ModalState {
    isModalOpen: boolean;
    id: number | null;
}

const ExoNotification = () => {
    document.title = "EXO Notification | Dashboard";

    const {hasPermission} = usePermissions();
    if (!hasPermission("view-echo-notifications")) return <Navigate to="/no-access"/>

    const queryClient = useQueryClient();

    const [modal, setModal] = useState<ModalState>({isModalOpen: false, id: null});
    const [modalTemplate, setModalTemplate] = useState<ModalState>({isModalOpen: false, id: null});
    const [modalReplacer, setModalReplacer] = useState<ModalState>({isModalOpen: false, id: null});

    const [filter, setFilter] = useState({per_page: 10, page: 1, sort_key: "id", sort_order: "desc", q: ""});

    const {data: exoList, isLoading, isError} = useQuery({
        queryKey: ["keyExoList", filter],
        queryFn: () => ExoService.getAll(filter)
    });

    const onSuccessRemove = () => {
        queryClient.invalidateQueries({queryKey: ["keyExoList", filter]})
    }

    const {mutate: removeExo} = useMutation({
        mutationFn: (id: number) => ExoService.remove(id),
        onSuccess: onSuccessRemove,
        mutationKey: ["keyRemoveExo"],
    });

    const updateFilter = useCallback((changes: Partial<typeof filter>) => {
        setFilter((prevFilter) => ({...prevFilter, ...changes}));
    }, [setFilter]);

    const handlePageSizeChange = useCallback((newPageSize: number) => updateFilter({
        per_page: newPageSize,
        page: 1
    }), [updateFilter])

    const handlePageChange = useCallback((newPage: number) => updateFilter({page: newPage}), [updateFilter])

    const handleSearchChange = useCallback((search: string) => {
        if (!search && !filter.q) return
        updateFilter({q: search, page: 1})
    }, [filter.q, updateFilter])

    const handleSortChange = useCallback((sortKey?: string, sortOrder?: string) => {
        updateFilter({sort_key: sortKey, sort_order: sortOrder, page: 1})
    }, [updateFilter])

    const handleModalSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyExoList", filter]})
            .then(() => setModal({isModalOpen: false, id: null}));
    }

    const handleAddClick = () => {
        setModal({isModalOpen: true, id: null});
    }

    const handleModalClose = () => {
        setModal({isModalOpen: false, id: null});
    }

    const handleUpdateClick = (id: number) => {
        setModal({isModalOpen: true, id});
    }

    const handleTemplateOpenClick = () => {
        setModalTemplate({isModalOpen: true, id: null});
    }

    const handleTemplateCloseClick = () => {
        setModalTemplate({isModalOpen: false, id: null});
    }

    const handleReplacerOpenClick = () => {
        setModalReplacer({isModalOpen: true, id: null});
    }

    const handleReplacerCloseClick = () => {
        setModalReplacer({isModalOpen: false, id: null});
    }

    const handleRemoveClick = (id: number) => {
        removeExo(id);
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    {/* Render Breadcrumbs */}
                    <Breadcrumbs title="Dashboard" breadcrumbItem="EXO Notification"/>
                    {(hasPermission("view-echo-templates") || hasPermission("create-echo-notifications")) &&
                        <Row className="align-items-center">
                            <Col md={12}>
                                <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
                                    {hasPermission("view-echo-replacers") &&
                                        <Button color="secondary" className="d-flex align-items-center"
                                                onClick={handleReplacerOpenClick}>
                                            <i className="bx bx-refresh me-1"></i> Replacer
                                        </Button>}
                                    {hasPermission("view-echo-templates") &&
                                        <Button color="secondary" className="d-flex align-items-center"
                                                onClick={handleTemplateOpenClick}>
                                            <i className="bx bx-book-bookmark me-1"></i> Templates
                                        </Button>}
                                    {hasPermission("create-echo-notifications") &&
                                        <Button color="light" className="d-flex align-items-center"
                                                onClick={handleAddClick}>
                                            <i className="bx bx-plus me-1"></i> Add New
                                        </Button>}
                                </div>
                            </Col>
                        </Row>}
                    <Row>
                        <Col lg="12">
                            <Card>
                                <CardBody className="border-bottom">
                                    <TableExo
                                        data={exoList?.data || []}
                                        pageSize={filter.per_page}
                                        onSearchChange={handleSearchChange}
                                        onPageSizeChange={handlePageSizeChange}
                                        onSortChange={handleSortChange}
                                        onUpdate={handleUpdateClick}
                                        onRemove={handleRemoveClick}
                                    />

                                    {!isLoading && exoList?.data?.length === 0 && (
                                        <div className="my-4"><NoSearchResult/></div>)}
                                    {isLoading && (<div className="my-4"><NoSearchResult text="Loading..."/></div>)}
                                    {isError && (<div className="my-4"><NoSearchResult/></div>)}

                                    {exoList && exoList.pagination.total_page > 1 && (
                                        <ServerPagination
                                            totalPages={exoList.pagination.total_page}
                                            page={exoList.pagination.current_page}
                                            onChange={handlePageChange}
                                            className="mt-2"/>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {modal.isModalOpen &&
                        <ModalExo
                            exoId={modal.id}
                            isOpen={modal.isModalOpen}
                            onSuccess={handleModalSuccess}
                            onClose={handleModalClose}/>}

                    {modalTemplate.isModalOpen &&
                        <ModalExoTemplate
                            isOpen={modalTemplate.isModalOpen}
                            onClose={handleTemplateCloseClick}/>}

                    {modalReplacer.isModalOpen &&
                        <ModalExoReplacer
                            isOpen={modalReplacer.isModalOpen}
                            onClose={handleReplacerCloseClick}/>}
                </Container>
            </div>
        </React.Fragment>
    );
}

export default ExoNotification;